import { isPraise } from '@/lib/who-match';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import {
  answeredPrayers,
  answeredYears,
  answeredInYear,
  daysCarried,
  answerMoment,
  whoOf,
  isSelf,
  formatShortDateTime,
} from '@/lib/prayer-utils';
import PrivateBadge from './PrivateBadge';
import UpdateSheet from './UpdateSheet';
import { useNavLayer } from '@/hooks/useNavLayer';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useVoiceCapture } from '@/hooks/useVoiceCapture';
import { isCoreReadOnly } from '@/lib/core-cutover';
import styles from './EvidenceBook.module.css';

/**
 * Hard cap per year per CLAUDE.md rule #20 amendment. Testimony cards
 * render the full prayer body and full testimony — variable height.
 * 50 most recent answered in a year is enough for a glanceable view;
 * "Show all N earlier in {year}" inline drilldown reveals the rest.
 */
const EVIDENCE_HARD_CAP = 50;

interface EvidenceBookProps {
  prayers: Prayer[];
  onClose: () => void;
  onNew: () => void;
  onPrayerTap: (id: string) => void;
  onOpenPerson?: (who: string) => void;
  /** Coordinator-computed z for the `evidence` layer root (A1). */
  zIndex?: number;
}

export default function EvidenceBook({
  prayers,
  onClose,
  onNew,
  onPrayerTap,
  onOpenPerson,
  zIndex,
}: EvidenceBookProps) {
  const { t } = useTranslation();

  const allAnswered = useMemo(() => answeredPrayers(prayers), [prayers]);
  const years = useMemo(() => answeredYears(prayers), [prayers]);
  const defaultYear = years[0] ?? new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(defaultYear);
  const activeYear = years.includes(selectedYear) ? selectedYear : defaultYear;
  const [editingTestimonyFor, setEditingTestimonyFor] = useState<string | null>(null);

  // A2: the inline testimony sheet is a history layer above `evidence`.
  // EvidenceBook mounts with editingTestimonyFor === null -> StrictMode-safe.
  // The layer close drops any in-flight recording on a Back-abandon (the
  // registerTarget unmount cleanup would otherwise stopListening = transcribe).
  const voice = useVoiceCapture();
  const zEvidenceTestimony = useNavLayer(
    'evidence-testimony',
    editingTestimonyFor !== null,
    () => {
      if (voice.isRecording || voice.isTranscribing) void voice.cancel();
      setEditingTestimonyFor(null);
    },
  );
  // App notice stack: the in-flow spacer below the header keeps `.canvas`
  // (year navigation, cards) below the fixed notices. Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const yearPrayers = useMemo(
    () => answeredInYear(prayers, activeYear),
    [prayers, activeYear],
  );

  // Variable-height testimony cards — hard-capped per CLAUDE.md rule
  // #20. Reset to capped on year change so navigating to another year
  // doesn't carry the previous expanded state.
  const [expandedYear, setExpandedYear] = useState<number | null>(null);
  const showAllOlder = expandedYear === activeYear;
  const olderCount = Math.max(0, yearPrayers.length - EVIDENCE_HARD_CAP);
  const visibleYearPrayers = (olderCount === 0 || showAllOlder)
    ? yearPrayers
    : yearPrayers.slice(0, EVIDENCE_HARD_CAP);

  const yearIdx = years.indexOf(activeYear);
  const canGoOlder = yearIdx >= 0 && yearIdx < years.length - 1;
  const canGoNewer = yearIdx > 0;

  const goToOlderYear = () => {
    if (canGoOlder) setSelectedYear(years[yearIdx + 1]);
  };
  const goToNewerYear = () => {
    if (canGoNewer) setSelectedYear(years[yearIdx - 1]);
  };

  return (
    <div className={styles.container} style={{ zIndex }} role="dialog" aria-modal="true" aria-label={t('evidence.title')}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('nav.back')}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="14 6 8 12 14 18" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.newBtn}
          onClick={onNew}
          aria-label={t('nav.newAria')}
        >
          {t('nav.new')}
        </button>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div className={styles.canvas}>
        {allAnswered.length === 0 ? (
          <p className={styles.empty}>{t('evidence.empty')}</p>
        ) : (
          <>
            <div className={styles.hero}>
              {years.length > 1 && (
                <div className={styles.yearNav}>
                  <button
                    type="button"
                    className={styles.yearArrow}
                    onClick={goToOlderYear}
                    disabled={!canGoOlder}
                    aria-label={t('evidence.olderYearAria')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  <span className={styles.year}>{activeYear}</span>
                  <button
                    type="button"
                    className={styles.yearArrow}
                    onClick={goToNewerYear}
                    disabled={!canGoNewer}
                    aria-label={t('evidence.newerYearAria')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              )}
              {years.length <= 1 && (
                <span className={styles.year}>{activeYear}</span>
              )}
              <h1 className={styles.headline}>
                {t('evidence.headlineCount', { count: yearPrayers.length, year: activeYear })}
              </h1>
              <p className={styles.psalm}>{t('evidence.psalm')}</p>
            </div>

            <ul className={styles.cardList}>
              {visibleYearPrayers.map((prayer) => (
                <TestimonyCard
                  key={prayer.id}
                  prayer={prayer}
                  onOpen={() => onPrayerTap(prayer.id)}
                  onOpenPerson={onOpenPerson}
                  onEditTestimony={() => setEditingTestimonyFor(prayer.id)}
                  t={t}
                />
              ))}
            </ul>
            {olderCount > 0 && !showAllOlder && (
              <button
                type="button"
                className={styles.drilldownBtn}
                onClick={() => setExpandedYear(activeYear)}
                aria-label={t('nav.showAllEarlierAria', { count: olderCount })}
              >
                {t('nav.showAllEarlierInYear', { count: olderCount, year: activeYear })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Cutover: never open the testimony editor, even from a stale state. */}
      {editingTestimonyFor && !isCoreReadOnly() && (() => {
        const prayer = prayers.find((p) => p.id === editingTestimonyFor);
        if (!prayer) return null;
        return (
          <TestimonyUpdateSheet
            prayer={prayer}
            onClose={() => setEditingTestimonyFor(null)}
            zIndex={zEvidenceTestimony}
            t={t}
          />
        );
      })()}
    </div>
  );
}

/* ---- Testimony Card ---- */

type TFn = (key: string, opts?: Record<string, unknown>) => string;

interface TestimonyCardProps {
  prayer: Prayer;
  onOpen: () => void;
  onOpenPerson?: (who: string) => void;
  onEditTestimony: () => void;
  t: TFn;
}

function TestimonyCard({ prayer, onOpen, onOpenPerson, onEditTestimony, t }: TestimonyCardProps) {
  const rawWho = whoOf(prayer);
  const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
  const canOpenPerson = Boolean(onOpenPerson) && rawWho !== 'General';
  const days = daysCarried(prayer);
  const updates = prayer.progressHistory?.length ?? 0;
  const answeredLabel = t('evidence.answeredOn', {
    date: formatShortDateTime(answerMoment(prayer)),
  });

  const handleOpenKey = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <li
      className={styles.card}
      onClick={onOpen}
      onKeyDown={handleOpenKey}
      role="button"
      tabIndex={0}
      aria-label={`${displayName}, ${answeredLabel}`}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <span className={styles.checkIcon} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          {canOpenPerson ? (
            <button
              type="button"
              className={styles.whoBtn}
              onClick={(e) => { e.stopPropagation(); onOpenPerson!(rawWho); }}
              aria-label={t('people.prayersFor', { name: displayName })}
            >
              {displayName}
            </button>
          ) : (
            <span className={styles.who}>{displayName}</span>
          )}
        </div>
        <span className={styles.answeredDate}>{answeredLabel}</span>
      </div>

      {prayer.isPrivate && (
        <div className={styles.cardPrivateBadge}>
          <PrivateBadge size="sm" />
        </div>
      )}

      <p className={styles.prayerBody}>{prayer.rawText}</p>

      <div className={styles.meta}>
        <span>{t('evidence.prayedOn', { date: formatShortDateTime(prayer.createdAt) })}</span>
        <span className={styles.metaDot} aria-hidden="true">·</span>
        <span>{t('evidence.carriedDays', { count: days })}</span>
        {updates > 0 && (
          <>
            <span className={styles.metaDot} aria-hidden="true">·</span>
            <span>{t('evidence.updatesCount', { count: updates })}</span>
          </>
        )}
      </div>

      {/* Cutover: writing or editing a testimony is a prayer write. The
          testimony TEXT still renders; only the edit affordance goes. */}
      {prayer.testimony ? (
        <div className={styles.testimonyDisplay}>
          <p className={styles.testimonyNote}>{prayer.testimony}</p>
          {!isCoreReadOnly() && (
          <button
            type="button"
            className={styles.testimonyEditLink}
            onClick={(e) => { e.stopPropagation(); onEditTestimony(); }}
          >
            {t('evidence.editTestimony')}
          </button>
          )}
        </div>
      ) : isCoreReadOnly() ? null : (
        <button
          type="button"
          className={styles.testimonyAddBtn}
          onClick={(e) => { e.stopPropagation(); onEditTestimony(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{t('evidence.addTestimonyCta')}</span>
        </button>
      )}
    </li>
  );
}

/* ---- Bottom sheet for capturing / editing a testimony (matches Garden's update sheet) ---- */

interface TestimonyUpdateSheetProps {
  prayer: Prayer;
  onClose: () => void;
  zIndex?: number;
  t: TFn;
}

function TestimonyUpdateSheet({ prayer, onClose, zIndex }: TestimonyUpdateSheetProps) {
  return (
    <UpdateSheet
      prayer={prayer}
      variant="testimony"
      voiceTargetPrefix="evidence-testimony"
      onClose={onClose}
      zIndex={zIndex}
    />
  );
}
